import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const milestones = [
    {
        year: '2009',
        title: 'Started with 600 Taps',
        desc: 'Wayside Maple got its start in 2009 with 600 taps and a commitment to doing things the right way from day one.',
        image: '/images/about/milestone-0.jpg',
        alt: 'Wayside Maple starter image for 2009',
    },
    {
        year: '2012',
        title: 'Up to 3700 Taps',
        desc: 'By 2012, the operation had grown to 3,700 taps, marking a major jump in production capacity and reach.',
        image: '/images/about/milestone-1.jpg',
        alt: 'Wayside Maple milestone from 2012',
    },
    {
        year: '2013',
        title: 'Upgraded 6x16 Evaporator',
        desc: 'In 2013, Wayside Maple upgraded to a 6x16 evaporator and increased the tap count to 4,900.',
        image: '/images/about/milestone-3.jpg',
        alt: 'Wayside Maple 6x16 evaporator upgrade in 2013',
    },
    {
        year: '2020',
        title: 'Expanded to a Larger Sugarhouse',
        desc: 'In 2020, the business expanded into a larger sugarhouse and reached 10,000 taps on vacuum.',
        image: '/images/about/milestone-2.jpg',
        alt: 'Wayside Maple milestone from 2020',
    },
    {
        year: '2026',
        title: 'Went Digital',
        desc: 'In 2026, Wayside Maple went digital, bringing the farm story and products online in a bigger way.',
        image: '/images/about/milestone-4.jpg',
        alt: 'Wayside Maple went digital in 2026',
    },
];

const AboutPage = () => {
    return (
        <div className="min-h-screen bg-white" data-testid="about-page">
            <SEO
                title="Our Story"
                description="Learn about Wayside Maple's journey crafting premium small-batch maple syrup in the hills of Troy, Pennsylvania since 2009. Family-owned, sustainably harvested, and rooted in tradition."
                keywords="Wayside Maple story, maple syrup farm, Troy Pennsylvania, family farm, sustainable maple syrup, maple syrup production"
                path="/about"
            />
            <Header />
            
            {/* Hero */}
            <section className="pt-32 pb-16 border-b border-black/10">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <span className="text-maple-red font-mono text-sm tracking-widest">
                        OUR STORY
                    </span>
                    <h1 className="font-anton text-5xl sm:text-6xl lg:text-7xl text-maple-dark mt-4">
                        ABOUT US
                    </h1>
                </div>
            </section>

            {/* Main Content */}
            <section className="py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                        {/* Image */}
                        <div className="relative">
                            <div className="aspect-[4/5] overflow-hidden border border-black/10">
                                <img 
                                    src="/images/about/about-hero.jpg"
                                    alt="Wayside Maple sugarhouse under a rainbow"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="absolute -bottom-6 -right-6 bg-maple-olive p-8 hidden md:block">
                                <span className="font-mono text-5xl text-white">2009</span>
                                <p className="font-manrope text-sm text-white/80 mt-2">Year Established</p>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="flex flex-col justify-center">
                            <h2 className="font-anton text-3xl sm:text-4xl text-maple-dark">
                                FROM THE HILLS OF TROY, PENNSYLVANIA
                            </h2>
                            <div className="mt-8 space-y-6 text-maple-dark/70 leading-relaxed">
                                <p>
                                    Wayside Maple was founded in 2009 with a simple mission: to produce the 
                                    finest quality maple syrup using traditional methods passed down through 
                                    generations.
                                </p>
                                <p>
                                    Nestled in the rolling hills of Troy, Pennsylvania, our family-operated 
                                    sugar bush spans over 50 acres of pristine maple forest. Each spring, 
                                    we tap our trees using time-honored techniques, carefully collecting 
                                    sap during the critical harvest window when conditions are just right.
                                </p>
                                <p>
                                    Our small-batch approach ensures that every bottle of Wayside Maple 
                                    syrup meets our exacting standards for flavor, color, and purity. 
                                    We believe that great maple syrup can't be rushed – it requires 
                                    patience, expertise, and a deep respect for the natural process.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Values Section */}
            <section className="py-16 md:py-24 bg-maple-secondary border-y border-black/10">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="text-center mb-16">
                        <span className="text-maple-red font-mono text-sm tracking-widest">
                            OUR VALUES
                        </span>
                        <h2 className="font-anton text-4xl sm:text-5xl text-maple-dark mt-4">
                            WHAT WE STAND FOR
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-white p-8 border-l-4 border-maple-red">
                            <h3 className="font-anton text-2xl text-maple-dark mb-4">QUALITY</h3>
                            <p className="text-maple-dark/60 leading-relaxed">
                                Every batch is tested and tasted to ensure it meets our rigorous standards 
                                before it ever reaches your table.
                            </p>
                        </div>
                        <div className="bg-white p-8 border-l-4 border-maple-olive">
                            <h3 className="font-anton text-2xl text-maple-dark mb-4">TRADITION</h3>
                            <p className="text-maple-dark/60 leading-relaxed">
                                We honor the craft of maple sugaring, using methods that have been perfected 
                                over generations.
                            </p>
                        </div>
                        <div className="bg-white p-8 border-l-4 border-maple-teal">
                            <h3 className="font-anton text-2xl text-maple-dark mb-4">SUSTAINABILITY</h3>
                            <p className="text-maple-dark/60 leading-relaxed">
                                Our practices ensure that our maple forest will continue to thrive for 
                                generations to come.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Timeline */}
            <section className="py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="text-center mb-16">
                        <span className="text-maple-red font-mono text-sm tracking-widest">
                            MILESTONES
                        </span>
                        <h2 className="font-anton text-4xl sm:text-5xl text-maple-dark mt-4">
                            OUR JOURNEY
                        </h2>
                    </div>

                    <div className="space-y-16 md:space-y-24">
                        {milestones.map((item, index) => {
                            const descriptionFirst = index % 2 === 0;

                            const descriptionBlock = (
                                <div className="flex flex-col justify-center order-2 md:order-none">
                                    <div className="max-w-xl">
                                        <h3 className="font-anton text-3xl sm:text-4xl text-maple-dark leading-tight">
                                            {item.title}
                                        </h3>
                                        <p className="text-maple-dark/70 mt-5 text-lg leading-relaxed">
                                            {item.desc}
                                        </p>
                                    </div>
                                </div>
                            );

                            const imageBlock = (
                                <div className="order-3 md:order-none">
                                    <div className="aspect-[4/3] overflow-hidden border border-black/10 bg-maple-secondary">
                                        {item.image ? (
                                            <img
                                                src={item.image}
                                                alt={item.alt}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center px-6 text-center">
                                                <span className="font-mono text-sm tracking-[0.25em] text-maple-dark/40 uppercase">
                                                    Image Coming Soon
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );

                            return (
                                <div key={item.year} className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-8 md:gap-12 items-center">
                                    {descriptionFirst ? descriptionBlock : imageBlock}

                                    <div className="order-1 md:order-none flex items-center justify-center">
                                        <div className="inline-flex items-center justify-center border border-black/10 bg-white px-6 py-3 min-w-[120px]">
                                            <span className="font-mono text-maple-red text-2xl sm:text-3xl tracking-[0.2em] pl-[0.2em]">
                                                {item.year}
                                            </span>
                                        </div>
                                    </div>

                                    {descriptionFirst ? imageBlock : descriptionBlock}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default AboutPage;
